import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { modifyUser } from "../../../store/thunks/userThunk";
import { fetchClinics } from "../../../store/thunks/clinicThunk";

const UserAvailabilityInformation = ({ user = {}, onComplete }) => {
  const dispatch = useDispatch();
  const clinics = useSelector((state) => state.clinics.clinics);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [availabilityPeriods, setAvailabilityPeriods] = useState([]);
  const [saveStatus, setSaveStatus] = useState({});
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [missingDates, setMissingDates] = useState([]);
  const [hasFullCoverage, setHasFullCoverage] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const daysOfWeek = [
    "Monday",
    "Tuesday", 
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  useEffect(() => {
    if (clinics.length === 0) {
      dispatch(fetchClinics());
    }
  }, [dispatch, clinics.length]);

  // Helper function to parse a date string as Eastern Time
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    console.log('Parsing date string:', dateStr);
    
    if (dateStr.includes('T')) {
      dateStr = dateStr.split('T')[0];
    }
    
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      console.error('Invalid date string format:', dateStr);
      return null;
    }

    const [year, month, day] = dateStr.split('-').map(Number);
    console.log('Parsed components:', { year, month, day });

    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.error('Invalid date components:', { year, month, day });
      return null;
    }

    if (month < 1 || month > 12 || day < 1 || day > 31) {
      console.error('Date components out of valid range:', { year, month, day });
      return null;
    }

    const date = new Date(year, month - 1, day);
    console.log('Resulting date object:', date);
    return date;
  };

  // Helper function to format date for display in Eastern Time
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    console.log('Formatting date string for display:', dateStr);
    const date = parseLocalDate(dateStr);
    if (!date) return '';
    
    const formatted = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      timeZone: 'America/New_York'
    });
    console.log('Formatted date:', formatted);
    return formatted;
  };

  useEffect(() => {
    if (!user?.availability) return;
    console.log('Processing user availability:', user.availability);

    const initialPeriods = user.availability.map(entry => ({
      clinic: entry.clinic,
      startDate: entry.startDate || "",
      endDate: entry.endDate || "",
      schedule: daysOfWeek.reduce((acc, day) => {
        acc[day] = entry.schedule[day] || [{ start: "", end: "" }];
        return acc;
      }, {})
    }));

    console.log('Initial periods before sorting:', initialPeriods);

    const sortedPeriods = initialPeriods.sort((a, b) => {
      if (!a.startDate || !b.startDate) return 0;
      console.log('Comparing dates:', { a: a.startDate, b: b.startDate });
      const dateA = parseLocalDate(a.startDate);
      const dateB = parseLocalDate(b.startDate);
      console.log('Parsed dates for comparison:', { dateA, dateB });
      return dateA - dateB;
    });

    console.log('Sorted periods:', sortedPeriods);
    setAvailabilityPeriods(sortedPeriods);
  }, [user]);

  useEffect(() => {
    if (!selectedClinic) {
      setMissingDates([]);
      setHasFullCoverage(false);
      return;
    }

    console.log('Checking coverage for clinic:', selectedClinic);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    console.log('Today:', today);

    const threeMonthsFromNow = new Date(today);
    threeMonthsFromNow.setMonth(today.getMonth() + 3);
    threeMonthsFromNow.setHours(23, 59, 59, 999);
    console.log('Three months from now:', threeMonthsFromNow);

    const missingDateRanges = [];
    let currentDate = new Date(today);

    while (currentDate <= threeMonthsFromNow) {
      const localDateStr = currentDate.toISOString().split('T')[0];
      console.log('Checking coverage for date:', localDateStr);
      
      const isDateCovered = availabilityPeriods.some(period => {
        if (!period.startDate || !period.endDate || period.clinic !== selectedClinic) {
          return false;
        }

        const periodStart = parseLocalDate(period.startDate);
        const periodEnd = parseLocalDate(period.endDate);
        console.log('Checking against period:', { start: periodStart, end: periodEnd });
        
        periodEnd.setDate(periodEnd.getDate() + 1);

        const isCovered = currentDate >= periodStart && currentDate < periodEnd;
        console.log('Date covered by this period:', isCovered);
        return isCovered;
      });

      if (!isDateCovered) {
        console.log('Date not covered:', localDateStr);
        missingDateRanges.push(localDateStr);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    console.log('Missing date ranges:', missingDateRanges);
    setMissingDates(missingDateRanges);
    setHasFullCoverage(missingDateRanges.length === 0);
  }, [availabilityPeriods, selectedClinic]);

  const handleAddPeriod = () => {
    const emptySchedule = daysOfWeek.reduce((acc, day) => {
      acc[day] = [{ start: "", end: "" }];
      return acc;
    }, {});

    const today = new Date();
    const endDate = new Date();
    endDate.setMonth(today.getMonth() + 1);

    console.log('Adding new period with dates:', {
      start: today.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    });

    const newPeriod = {
      clinic: selectedClinic,
      startDate: today.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      schedule: emptySchedule
    };

    setAvailabilityPeriods(prev => [...prev, newPeriod]);
    const newPeriodIndex = availabilityPeriods.length;
    setExpandedPeriods(prev => ({ ...prev, [newPeriodIndex]: true }));
    setEditingPeriod(newPeriodIndex);
  };

  const handleRemovePeriod = (index) => {
    console.log('Removing period at index:', index);
    const updatedPeriods = availabilityPeriods.filter((_, i) => i !== index);
    console.log('Updated periods after removal:', updatedPeriods);
    
    setAvailabilityPeriods(updatedPeriods);
    setExpandedPeriods(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    setEditingPeriod(null);

    dispatch(
      modifyUser({
        userId: user._id || user.userId,
        userData: {
          availability: updatedPeriods
        }
      })
    ).then(() => {
      onComplete && onComplete(updatedPeriods);
    }).catch(error => {
      console.error("Failed to update user after removing period:", error);
    });
  };

  const handlePeriodChange = (index, field, value) => {
    console.log('Changing period:', { index, field, value });
    setAvailabilityPeriods(prev => {
      const updated = prev.map((period, i) =>
        i === index ? { ...period, [field]: value } : period
      );
      console.log('Updated periods after change:', updated);
      return updated;
    });
  };

  const handleAddTimeSlot = (periodIndex, day) => {
    console.log('Adding time slot:', { periodIndex, day });
    setAvailabilityPeriods(prev => prev.map((period, i) => {
      if (i === periodIndex) {
        return {
          ...period,
          schedule: {
            ...period.schedule,
            [day]: [...period.schedule[day], { start: "", end: "" }]
          }
        };
      }
      return period;
    }));
  };

  const handleRemoveTimeSlot = (periodIndex, day, slotIndex) => {
    console.log('Removing time slot:', { periodIndex, day, slotIndex });
    setAvailabilityPeriods(prev => prev.map((period, i) => {
      if (i === periodIndex) {
        return {
          ...period,
          schedule: {
            ...period.schedule,
            [day]: period.schedule[day].filter((_, j) => j !== slotIndex)
          }
        };
      }
      return period;
    }));
  };

  const handleTimeChange = (periodIndex, day, slotIndex, field, value) => {
    console.log('Changing time:', { periodIndex, day, slotIndex, field, value });
    setAvailabilityPeriods(prev => prev.map((period, i) => {
      if (i === periodIndex) {
        const updatedSchedule = {
          ...period.schedule,
          [day]: period.schedule[day].map((slot, j) =>
            j === slotIndex ? { ...slot, [field]: value } : slot
          )
        };
        return { ...period, schedule: updatedSchedule };
      }
      return period;
    }));
  };

  const checkForOverlap = (periodToCheck, periodIndex) => {
    console.log('Checking for overlap:', { periodToCheck, periodIndex });
    const start = parseLocalDate(periodToCheck.startDate);
    const end = parseLocalDate(periodToCheck.endDate);

    return availabilityPeriods.some((period, index) => {
      if (index === periodIndex || period.clinic !== periodToCheck.clinic) return false;
      
      const otherStart = parseLocalDate(period.startDate);
      const otherEnd = parseLocalDate(period.endDate);
      
      const hasOverlap = (start <= otherEnd && end >= otherStart);
      console.log('Overlap check result:', { 
        periodIndex: index, 
        hasOverlap,
        start,
        end,
        otherStart,
        otherEnd
      });
      return hasOverlap;
    });
  };

  const handleSubmit = (periodIndex) => {
    console.log('Submitting period:', periodIndex);
    const periodToSave = availabilityPeriods[periodIndex];

    if (!periodToSave.startDate || !periodToSave.endDate) {
      console.log('Missing dates in period:', periodToSave);
      setSaveStatus(prev => ({ ...prev, [periodIndex]: "error" }));
      alert("Please set both start and end dates");
      return;
    }

    if (checkForOverlap(periodToSave, periodIndex)) {
      console.log('Overlap detected for period:', periodToSave);
      setSaveStatus(prev => ({ ...prev, [periodIndex]: "error" }));
      alert("This period overlaps with another period for the same clinic");
      return;
    }

    setSaveStatus(prev => ({ ...prev, [periodIndex]: "saving" }));

    const sortedPeriods = [...availabilityPeriods].sort((a, b) => {
      if (!a.startDate || !b.startDate) return 0;
      return parseLocalDate(a.startDate) - parseLocalDate(b.startDate);
    });

    console.log('Sorted periods before save:', sortedPeriods);

    const formattedPeriods = sortedPeriods.map(period => ({
      clinic: period.clinic,
      startDate: period.startDate,
      endDate: period.endDate,
      schedule: daysOfWeek.reduce((acc, day) => {
        acc[day] = period.schedule[day] || [{ start: "", end: "" }];
        return acc;
      }, {})
    }));

    console.log('Formatted periods for save:', formattedPeriods);

    dispatch(
      modifyUser({
        userId: user._id || user.userId,
        userData: {
          availability: formattedPeriods
        }
      })
    )
      .then(() => {
        console.log('Save successful');
        setAvailabilityPeriods(formattedPeriods);
        setSaveStatus(prev => ({ ...prev, [periodIndex]: "saved" }));
        setEditingPeriod(null);
        onComplete && onComplete(formattedPeriods);
        
        // Show success modal
        setShowSuccessModal(true);
        
        setTimeout(() => {
          setSaveStatus(prev => {
            const updated = { ...prev };
            delete updated[periodIndex];
            return updated;
          });
          setShowSuccessModal(false);
        }, 2000);
      })
      .catch((error) => {
        console.error('Save failed:', error);
        setSaveStatus(prev => ({ ...prev, [periodIndex]: "error" }));
      });
  };

  const togglePeriod = (index) => {
    console.log('Toggling period:', index);
    setExpandedPeriods(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getClinicName = (clinicId) => {
    const clinic = clinics.find(c => c._id === clinicId);
    return clinic ? clinic.name : 'Unknown Clinic';
  };

  return (
    <div className="space-y-6">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Success</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Availability information updated successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="pb-4 border-b border-gray-100">
        <h2 className="text-lg font-medium text-gray-900">Availability Information</h2>
        <p className="text-sm text-gray-500 mt-1">Manage user availability schedules by clinic</p>
      </div>

      {/* Clinic Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Clinic
        </label>
        <select
          className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          onChange={(e) => setSelectedClinic(e.target.value)}
          value={selectedClinic || ""}
        >
          <option value="">Select a clinic</option>
          {clinics.map((clinic) => (
            <option key={clinic._id} value={clinic._id}>
              {clinic.name}
            </option>
          ))}
        </select>
      </div>

      {selectedClinic && (
        <>
          {/* Coverage Status */}
          <div className={`p-4 rounded-sm border ${hasFullCoverage ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {hasFullCoverage ? (
                  <svg className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${hasFullCoverage ? 'text-green-800' : 'text-red-800'}`}>
                  {hasFullCoverage
                    ? 'Full availability coverage for the next 3 months'
                    : 'Missing availability coverage for some dates'}
                </h3>
                {!hasFullCoverage && missingDates.length > 0 && (
                  <div className="mt-2 text-sm text-red-700">
                    <p>Missing coverage for {missingDates.length} dates in the next 3 months</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Add Period Button */}
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-900">Availability Periods</h3>
            <button
              type="button"
              onClick={handleAddPeriod}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-sm text-white bg-gray-900 hover:bg-gray-800 transition-colors"
            >
              Add Period
            </button>
          </div>

          {/* Availability Periods */}
          <div className="space-y-3">
            {availabilityPeriods.map((period, periodIndex) => (
              <div key={periodIndex} className="border border-gray-200 rounded-sm">
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => togglePeriod(periodIndex)}
                >
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatDateForDisplay(period.startDate)} - {formatDateForDisplay(period.endDate)}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
                      ({getClinicName(period.clinic)})
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {editingPeriod === periodIndex && (
                      <span className="text-xs text-blue-600 font-medium">Editing</span>
                    )}
                    <svg
                      className={`w-4 h-4 text-gray-400 transform transition-transform ${expandedPeriods[periodIndex] ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {expandedPeriods[periodIndex] && (
                  <div className="px-3 pb-3 border-t border-gray-100">
                    {/* Period Controls */}
                    <div className="flex justify-end space-x-2 mb-3 pt-3">
                      <button
                        type="button"
                        onClick={() => setEditingPeriod(editingPeriod === periodIndex ? null : periodIndex)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {editingPeriod === periodIndex ? 'Cancel Edit' : 'Edit'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemovePeriod(periodIndex)}
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                      >
                        Remove
                      </button>
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={period.startDate}
                          onChange={(e) => handlePeriodChange(periodIndex, "startDate", e.target.value)}
                          disabled={editingPeriod !== periodIndex}
                          className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={period.endDate}
                          onChange={(e) => handlePeriodChange(periodIndex, "endDate", e.target.value)}
                          disabled={editingPeriod !== periodIndex}
                          className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                    </div>

                    {/* Schedule */}
                    <div className="space-y-3">
                      {daysOfWeek.map((day) => (
                        <div key={day}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-medium text-gray-700">{day}</h4>
                            {editingPeriod === periodIndex && (
                              <button
                                type="button"
                                onClick={() => handleAddTimeSlot(periodIndex, day)}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Add Time
                              </button>
                            )}
                          </div>
                          <div className="space-y-1">
                            {period.schedule[day].map((slot, slotIndex) => (
                              <div key={slotIndex} className="flex items-center space-x-2">
                                <input
                                  type="time"
                                  value={slot.start}
                                  onChange={(e) => handleTimeChange(periodIndex, day, slotIndex, "start", e.target.value)}
                                  disabled={editingPeriod !== periodIndex}
                                  className="px-2 py-1 text-xs border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                                />
                                <span className="text-xs text-gray-500">to</span>
                                <input
                                  type="time"
                                  value={slot.end}
                                  onChange={(e) => handleTimeChange(periodIndex, day, slotIndex, "end", e.target.value)}
                                  disabled={editingPeriod !== periodIndex}
                                  className="px-2 py-1 text-xs border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                                />
                                {editingPeriod === periodIndex && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveTimeSlot(periodIndex, day, slotIndex)}
                                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Save Button */}
                    {editingPeriod === periodIndex && (
                      <div className="flex justify-end pt-3 mt-3 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => handleSubmit(periodIndex)}
                          disabled={saveStatus[periodIndex] === "saving"}
                          className={`inline-flex justify-center px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                            saveStatus[periodIndex] === "saving"
                              ? "bg-gray-400 text-white cursor-not-allowed"
                              : saveStatus[periodIndex] === "saved"
                              ? "bg-green-600 text-white"
                              : saveStatus[periodIndex] === "error"
                              ? "bg-red-600 text-white"
                              : "bg-gray-900 text-white hover:bg-gray-800"
                          }`}
                        >
                          {saveStatus[periodIndex] === "saving"
                            ? "Saving..."
                            : saveStatus[periodIndex] === "saved"
                            ? "Saved!"
                            : saveStatus[periodIndex] === "error"
                            ? "Error"
                            : "Save Changes"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {availabilityPeriods.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No availability periods defined for this clinic.</p>
                <p className="text-xs mt-1">Click "Add Period" to get started.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserAvailabilityInformation;
